/**
 * Hook pour les feature flags de la société courante
 *
 * Récupère les features activées selon le BusinessType de la société.
 * Utilisé pour adapter l'UX : masquer/afficher des champs dans les devis, factures, etc.
 *
 * Architecture: Component → Hook → companiesAPI → GET /api/core/company/features
 */

import { useQuery } from '@tanstack/react-query';
import { companiesAPI } from '../utils/api';
import { useAuthStore } from '../stores/authStore';

/** Query key pour invalidation cohérente */
export const companyFeaturesKeys = {
  all: ['company-features'] as const,
  current: () => [...companyFeaturesKeys.all, 'current'] as const,
};

/** Type retourné par le hook */
export interface CompanyFeatures {
  unitOfMeasure: boolean;
  referenceSKU: boolean;
  sections: boolean;
  lineDiscount: boolean;
  depositInvoice: boolean;
  decennaleInsurance: boolean;
  latePenalties: boolean;
  reverseChargeVAT: boolean;
  compositeProducts: boolean;
  tieredPricing: boolean;
  shippingCosts: boolean;
  progressInvoice: boolean;
  retentionGuarantee: boolean;
  ouvrageLibrary: boolean;
}

export interface CompanyFeaturesData {
  businessType: string;
  companyTrade: string | null;
  tradeConfig: {
    businessType: string;
    defaultUnits: string[];
    decennaleRequired: boolean;
    reverseChargeEligible: boolean;
    defaultVatRate: number;
    category: string;
  } | null;
  features: CompanyFeatures;
}

/**
 * Hook useCompanyFeatures
 *
 * Retourne les feature flags de la société courante.
 * Les données sont cachées 5 minutes car elles changent rarement.
 *
 * @example
 * const { features, businessType, isLoading } = useCompanyFeatures();
 * if (features?.sections) { // Afficher les sections de devis }
 */
export function useCompanyFeatures() {
  const user = useAuthStore((s) => s.user);
  const hasCompany = !!user?.companyId;

  const { data, isLoading, error, refetch } = useQuery<CompanyFeaturesData>({
    queryKey: companyFeaturesKeys.current(),
    queryFn: () => companiesAPI.getFeatures(),
    enabled: hasCompany,
    staleTime: 5 * 60 * 1000, // 5 minutes — les features changent rarement
    refetchOnWindowFocus: false,
  });

  return {
    /** Feature flags de la société */
    features: data?.features ?? null,
    /** Type d'activité (SERVICE, ARTISAN_BTP, etc.) */
    businessType: data?.businessType ?? null,
    /** Sous-métier (ELECTRICIEN, PLOMBIER, etc.) */
    companyTrade: data?.companyTrade ?? null,
    /** Configuration du métier (unités, décennale, etc.) */
    tradeConfig: data?.tradeConfig ?? null,
    /** Données complètes */
    data: data ?? null,
    /** Chargement en cours */
    isLoading,
    /** Erreur éventuelle */
    error: error?.message ?? null,
    /** Recharger les features */
    refetch,
  };
}

/**
 * Helper pour vérifier si une feature est activée
 *
 * @example
 * const { features } = useCompanyFeatures();
 * if (isFeatureEnabled(features, 'sections')) { ... }
 */
export function isFeatureEnabled(
  features: CompanyFeatures | null,
  feature: keyof CompanyFeatures
): boolean {
  return features?.[feature] ?? false;
}
